import {
  CarouselComponent,
  FlipCardComponent,
  HotspotComponent,
  InteractiveComponent,
  LessonComponentBase,
  LessonDisplayMode
} from '../models/LessonComponent';
import { RgbaColor, Spacing } from '../models/LessonComponentStyle';
import { isEmpty, isNil, isNull } from 'lodash';

import AssetService from '../../shared/services/AssetService';
import { AudioVideoProgress } from '../models/AudioVideoProgress';
import { ItemModel } from '../../content/models/ItemModel';
import { LessonComponentType } from '../models/LessonComponentType';
import LessonService from './LessonService';
import { LessonType } from '../../dashboard/models/LessonVm';
import { SelectionOption } from '../models/LessonComponentContent';
import { getColorString } from './StyleService';
import { nanoid } from 'nanoid';

class LessonComponentService {
  /** Duration, in seconds, where the end of the video counts as played. */
  private VIDEO_ENDED_GRACE_s = 5;

  public isInteractive(component: LessonComponentBase) {
    switch (component.type) {
      case 'single_selection':
      case 'multiple_selection':
      case 'single_selection_card':
      case 'multiple_selection_card':
      case 'free_text':
      case 'sorter':
      case 'video_uploader':
      case 'fill_blank':
      case 'fill_menu':
      case 'sequencer':
      case 'slider':
      case 'file_uploader':
      case 'screen_capture':
      case 'screen_capture_comparison':
        return true;
      default:
        return false;
    }
  }

  public hasAnswer(component: LessonComponentBase) {
    switch (component.type) {
      case 'single_selection':
      case 'multiple_selection':
      case 'single_selection_card':
      case 'multiple_selection_card':
      case 'free_text':
      case 'video':
      case 'audio':
      case 'sorter':
      case 'video_uploader':
      case 'full_width_video':
      case 'carousel':
      case 'hotspot':
      case 'fill_blank':
      case 'fill_menu':
      case 'sequencer':
      case 'slider':
      case 'file_uploader':
      case 'screen_capture':
      case 'screen_capture_comparison':
      case 'flip_card':
        return true;
      default:
        return false;
    }
  }

  public isAudioVideoType(type: LessonComponentType) {
    switch (type) {
      case 'video':
      case 'audio':
      case 'full_width_video':
        return true;
      default:
        return false;
    }
  }

  public isAutoMarking(type: LessonComponentType) {
    switch (type) {
      case 'single_selection':
      case 'multiple_selection':
      case 'single_selection_card':
      case 'multiple_selection_card':
      case 'sequencer':
      case 'sorter':
      case 'fill_blank':
      case 'fill_menu':
      case 'slider':
        return true;
      default:
        return false;
    }
  }

  public isNonAutoMarking(component: LessonComponentBase) {
    return this.isInteractive(component) && !this.isAutoMarking(component.type);
  }

  public getSelectionOptionValue() {
    return nanoid();
  }

  public randomizeSelectionOptions(options: SelectionOption[]) {
    const withRandomValue = options?.map((o) => ({
      ...o,
      randomValue: Math.random()
    }));

    return withRandomValue.sort((a, b) => a.randomValue - b.randomValue);
  }

  public randomizeItems<T>(items: T[]) {
    //* Using Fisher-Yates algorithm https://bost.ocks.org/mike/shuffle/
    let m = items.length;
    while (m) {
      const i = Math.floor(Math.random() * m--);
      [items[m], items[i]] = [items[i], items[m]];
    }
  }

  public getColorCss(color: RgbaColor) {
    if (!color) {
      return 'white';
    }
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
  }

  public getSpacingCss(spacing: Spacing) {
    if (!spacing) {
      return '0px';
    }
    return `${spacing.top}px ${spacing.right}px ${spacing.bottom}px ${spacing.left}px`;
  }

  public getLockPosition(
    components: LessonComponentBase[],
    lessonType: LessonType
  ) {
    let lockingPosition = -1;

    if (isEmpty(components)) {
      return lockingPosition;
    }

    for (const [index, component] of components.entries()) {
      if (this.hasAnswer(component) && component.required) {
        if (this.isAudioVideoType(component.type)) {
          if (!this.audioVideoEnded(component.answer)) {
            lockingPosition = index;
            //* audio / video not played to the end, lock the lesson!
            break;
          }
        } else if (component.type === 'carousel') {
          const carouselComponent = component as CarouselComponent;
          const itemIds = carouselComponent.content.carousel.map((i) => i.id);
          if (!this.hasVisited(component.answer, itemIds)) {
            lockingPosition = index;
            break;
          }
        } else if (component.type === 'hotspot') {
          const hotspotComponent = component as HotspotComponent;
          const itemIds = hotspotComponent.content.hotspot.map((i) => i.id);
          if (!this.hasVisited(component.answer, itemIds)) {
            lockingPosition = index;
            break;
          }
        } else if (component.type === 'flip_card') {
          const flipCardComponent = component as FlipCardComponent;
          const itemIds = flipCardComponent.content.flipCard.map((i) => i.id);
          if (!this.hasVisited(component.answer, itemIds)) {
            lockingPosition = index;
            break;
          }
        } else {
          if (isNil(component.answer)) {
            lockingPosition = index;
            //* not answered, lock the lesson!
            break;
          } else {
            //* answered
            const points = (component as InteractiveComponent).settings?.points;
            if (
              lessonType === 'lesson' && //* Only 'Basic' type locks the lesson with wrong answer.
              this.isAutoMarking(component.type) &&
              points > 0 && //* author's set points to the question
              component.mark === 0 //* wrong answer
            ) {
              lockingPosition = index;
              break;
            }
            //* If the last component is a required one, set the lockingPosition as components length
            if (lockingPosition === components.length - 1) {
              lockingPosition = components.length;
            }
          }
        }
      }
    }
    return lockingPosition;
  }

  private hasVisited(answers: string, visitedIds: string[]) {
    //* All visited carousel/hotspot/flipcard items are stored as an answer in JSON.
    if (answers) {
      const visitedItems: string[] = JSON.parse(answers).map(
        (v: ItemModel) => v.itemId
      );
      return visitedIds.every((c) => visitedItems.includes(c));
    }
    return false;
  }

  public getLessonTotalPoints(
    lessonType: LessonType,
    components: LessonComponentBase[]
  ) {
    return (
      components
        ?.filter(
          (c) =>
            (LessonService.isAutoMarkingLesson(lessonType) && //* In 'Basic' and 'Quiz' type lesson, exclude non-automaking components.
              this.isAutoMarking(c.type)) ||
            (lessonType === 'assessment' && this.isInteractive(c)) //* In 'Assessment' type lesson, count all interactive components.
        )
        ?.map((c) => (c as InteractiveComponent).settings?.points)
        ?.reduce(
          (total, value) => total + value,
          0 /* providing initial value in case array is empty */
        ) ?? 0
    );
  }

  public getUserTotalPoints(components: LessonComponentBase[]) {
    return (
      components
        ?.map((c) => c.mark ?? 0)
        ?.reduce(
          (total, value) => total + value,
          0 /* providing initial value in case array is empty */
        ) ?? 0
    );
  }

  private audioVideoEnded(answer: string) {
    //* The video progress data is stored as an answer in JSON.
    try {
      const progress: AudioVideoProgress = JSON.parse(answer);
      return (
        progress.playedTime > 0 &&
        progress.playedTime >= progress.duration - this.VIDEO_ENDED_GRACE_s
      );
    } catch {
      return false;
    }
  }

  public getFilteredContent(content: string): string {
    if (content) {
      const element = document.createElement('div');
      element.innerHTML = content;
      /* checks for the whitespace in the last element child (if it is <p> element) and removes that element */
      if (
        /^\s*$/.test(element.lastElementChild?.textContent) &&
        element.lastElementChild?.nodeName === 'P'
      ) {
        element.removeChild(element.lastElementChild);
        return this.getFilteredContent(element.innerHTML);
      }
      return element.innerHTML;
    }
    return content;
  }

  /** Remove all html tags from the given html string */
  public stripTags(src: string): string {
    const el = document.createElement('span');
    el.innerHTML = src;
    return el.textContent;
  }

  /** Remove all html tags from the given html string and return first element */
  public cleanAndGetFirst(src: string): string {
    const el = document.createElement('span');
    el.innerHTML = src;
    return el.firstChild.textContent;
  }

  public isLessonLocked(
    mode: LessonDisplayMode,
    finalized: boolean,
    quizLocked: boolean,
    lessonType: LessonType,
    assessmentSubmitted: boolean,
    portfolioLocked: boolean
  ) {
    const lessonLocked =
      mode !== 'edit' &&
      ((quizLocked && !finalized) /* check whether quiz is locked */ ||
        (lessonType === 'assessment' &&
          assessmentSubmitted) /* check whether assessment is submitted or locked */ ||
        portfolioLocked);
    return lessonLocked;
  }

  public isLessonComponentLocked(
    component: LessonComponentBase,
    mode: LessonDisplayMode,
    correct: boolean,
    reattempt: boolean,
    finalized: boolean,
    quizLocked: boolean,
    lessonType: LessonType,
    assessmentSubmitted: boolean,
    portfolioLocked: boolean
  ) {
    const lessonLocked = this.isLessonLocked(
      mode,
      finalized,
      quizLocked,
      lessonType,
      assessmentSubmitted,
      portfolioLocked
    );

    // Enable category items navigation for marker. Sorter has it's own `disable` prop for draggable items.
    if (
      mode === 'mark' &&
      this.isInteractive(component) && // This checks all interactive components(including unanswered components) in marking mode.
      this.isAutoMarking(component.type)
    ) {
      return component.type !== 'sorter';
    }

    return (
      ((mode !== 'edit' &&
        !isNull(correct) &&
        !reattempt) /* check already answered but not reattempt */ ||
        finalized /* check whether it is finalized  */ ||
        lessonLocked) /* check whether quiz locked or assessment locked */ &&
      /* check whether it is auto marking */
      this.isInteractive(component) &&
      this.isAutoMarking(component.type)
    );
  }

  public hasFullwidthComponent(components: LessonComponentBase[]) {
    if (!components) {
      return false;
    }
    return components.some(
      (c) => c.type === 'full_width_image' || c.type === 'full_width_video'
    );
  }

  public isFullwidthComponent(type: LessonComponentType) {
    if (!type) {
      return false;
    }
    return type === 'full_width_image' || type === 'full_width_video';
  }

  public isStretchBackground(component: LessonComponentBase) {
    return component?.style?.stretchBackground;
  }

  public hasStretchBackground(components: LessonComponentBase[]) {
    return components?.some((c) => this.isStretchBackground(c));
  }

  public canSubmit(mode: LessonDisplayMode) {
    return mode === 'learn' || mode === 'preview';
  }

  public hasRandomizeOptions(type: LessonComponentType) {
    switch (type) {
      case 'single_selection':
      case 'multiple_selection':
      case 'single_selection_card':
      case 'multiple_selection_card':
      case 'slider':
        return true;
      default:
        return false;
    }
  }

  public getTableRowBorder(color: RgbaColor) {
    return `1px solid ${getColorString(color)}`;
  }

  public tableRowBorderStyle(
    color: RgbaColor,
    cellIndex: number,
    backgroundColor?: RgbaColor,
    top = false
  ) {
    return {
      borderTop: top ? this.getTableRowBorder(color) : 0,
      borderBottom: this.getTableRowBorder(color),
      borderLeft: cellIndex === 0 ? this.getTableRowBorder(color) : 0,
      borderRight: this.getTableRowBorder(color),
      backgroundColor: getColorString(backgroundColor)
    };
  }

  public optionBorderColor(
    optionValue: string,
    selected: string[],
    primaryColor: string
  ) {
    const outlineColor = selected?.includes(optionValue) && primaryColor;
    return { outlineColor };
  }

  public canAddToSidePanel(type: LessonComponentType): boolean {
    return type === 'heading' || type === 'subheading';
  }

  /** Get url with new sas token when there is any media-link in the text  */
  public getUrlWithSas(text: string) {
    //Find assetkey and get url with new sas token and update href with the new url
    const dom = new DOMParser().parseFromString(text, 'text/html');
    const elementList = dom.querySelectorAll('.media-link');
    elementList.forEach((element) => {
      const assetkey = element.getAttribute('data-assetkey');
      const url = AssetService.getAssetUrl(assetkey);
      element.setAttribute('href', url);
    });
    const contentText = elementList.length
      ? dom.documentElement.innerHTML
      : text;
    return contentText;
  }
}

export default new LessonComponentService();
